import * as React from "react";
import { RouterLink as NextLink } from "../../router";
import { Card, CardContent } from "../../card";
import { Button } from "../../button";
import { Icon } from "../../icon";
import { Link } from "../link";
import { IntentBadge } from "../status-badge";
import { EmptyState } from "../empty-state";
import { HealthSparkline, type HealthAnnotation, type HealthArea } from "./health-context";
import type { FocusItem } from "./focus-list";
import { FocusDeployButton } from "./focus-deploy-button";
import { cn } from "../../utils";
import type { Intent } from "../../tokens/intents";

/**
 * FocusSections — the per-domain-sections surface below the Pulse (the approved
 * /styleguide/focus-sections "grouped by mode" model). Replaces the single
 * identity-column FocusHealth card.
 *
 * The MODE is the section; each below-target / not-started DOMAIN (a Pulse band)
 * is a self-contained Card within it: the domain header (tag + big health number
 * + Open), a LARGE annotated health graph up top, then its items. Work that maps
 * to no failing band — the next-best-actions — lands in one labelled "Across the
 * workspace" card, never a nameless tail. Two no-graph slots carry real blank
 * states: a not-started domain shows a GHOST of the trend that's coming; a
 * cross-cutting domain names the areas it touches with their live health dots.
 *
 * Presentational only. The assembler (buildFocusSections) does the categorization
 * server-side and passes the grouped structure in.
 */

const TARGET = 70;

const NUM: Record<Intent, string> = {
  success: "text-success",
  warning: "text-warning",
  danger: "text-danger",
  info: "text-info",
  neutral: "text-muted-foreground",
};
const DOT: Record<Intent, string> = {
  success: "bg-success",
  warning: "bg-warning",
  danger: "bg-danger",
  info: "bg-info",
  neutral: "bg-muted-foreground",
};
const ANN_TEXT: Record<HealthAnnotation["kind"], string> = {
  cause: "text-danger",
  fix: "text-success",
  pending: "text-muted-foreground",
};
const ANN_ICON: Record<HealthAnnotation["kind"], string> = {
  cause: "arrow-down",
  fix: "arrow-up",
  pending: "clock",
};

export type FocusDomainKind = "atrisk" | "setup" | "crosscutting";

export interface FocusSpan {
  key: string;
  label: string;
  intent: Intent;
}

export interface FocusSectionDomain {
  key: string;
  kind: FocusDomainKind;
  label: string;
  icon?: string;
  /** Health intent — drives the tag tint + number color (neutral for setup/cross). */
  intent: Intent;
  health?: number;
  trend?: number;
  /** Oldest→newest samples (empty for setup/cross). */
  history: { on: string; health: number }[];
  annotations: HealthAnnotation[];
  /** Setup-only: onboarding progress. */
  setup?: { done: number; total: number };
  /** Cross-cutting-only: the areas this work touches, each with its live intent. */
  spans?: FocusSpan[];
  items: FocusItem[];
  href?: string;
}

export interface FocusSectionGroup {
  modeKey: string;
  modeLabel: string;
  /** Count of at-risk domains in the mode (for the header sub-label). */
  atRiskCount: number;
  domains: FocusSectionDomain[];
}

export interface FocusSectionsProps {
  groups: FocusSectionGroup[];
  /** The single "Across the workspace" card (unmatched next-best-actions). */
  crosscutting?: FocusSectionDomain;
  className?: string;
}

// ── shared atoms ─────────────────────────────────────────────────────────────

/** Adapt a domain to the HealthArea shape HealthSparkline reads (atrisk only). */
function domainAsArea(domain: FocusSectionDomain): HealthArea {
  return {
    key: domain.key,
    label: domain.label,
    health: domain.health ?? 0,
    intent: domain.intent,
    history: domain.history,
    annotations: domain.annotations,
  };
}

function AreaTag({ domain }: { domain: FocusSectionDomain }) {
  return (
    <IntentBadge intent={domain.intent}>
      {domain.icon ? <Icon name={domain.icon} size="xs" /> : null}
      {domain.label}
    </IntentBadge>
  );
}

function Magnitude({ domain }: { domain: FocusSectionDomain }) {
  if (domain.kind === "setup") return <span className="type-body text-muted-foreground">· not started</span>;
  if (domain.kind === "crosscutting") return <span className="type-body text-muted-foreground">· not tied to one area</span>;
  return (
    <span className="inline-flex items-baseline gap-1.5">
      <span className={cn("type-heading font-medium tabular-nums", NUM[domain.intent])}>{domain.health}</span>
      <span className="type-subtext text-muted-foreground/60">/ {TARGET}</span>
      {domain.trend != null && domain.trend < 0 ? (
        <span className="inline-flex items-center gap-0.5 self-center type-subtext tabular-nums text-muted-foreground">
          <Icon name="arrow-down" size="xs" />
          {Math.abs(domain.trend)}
        </span>
      ) : null}
    </span>
  );
}

function LatestNote({ domain }: { domain: FocusSectionDomain }) {
  const a =
    domain.annotations.find((x) => x.kind === "cause") ??
    domain.annotations.find((x) => x.kind === "fix") ??
    domain.annotations.at(-1);
  if (!a) return null;
  return (
    <span className="inline-flex items-center gap-1.5 type-subtext">
      <Icon name={ANN_ICON[a.kind]} size="xs" className={cn("shrink-0", ANN_TEXT[a.kind])} />
      <span className="text-muted-foreground">
        <span className="text-foreground">{a.when}</span> · {a.text}
      </span>
    </span>
  );
}

function FocusActionRow({ item }: { item: FocusItem }) {
  // A fix backed by a one-click activation (a draft flow to turn on, a worker to
  // deploy) carries `deploy` and POSTs in place — the row is NOT a navigation, so
  // the "Turn on" button actually turns it on (matching the Pulse wall/board). A
  // navigation fix keeps the stretched-link row. Mirrors PulseBoard's WorkItem.
  if (item.deploy) {
    return (
      <div className="-mx-2 flex items-center gap-2.5 rounded-md px-2 py-1.5">
        <span className={cn("size-1.5 shrink-0 rounded-full", DOT[item.intent])} aria-hidden />
        <span className="min-w-0 flex-1 truncate type-body">
          <span className="text-foreground">{item.title}</span>
          {item.meta ? <span className="text-muted-foreground"> · {item.meta}</span> : null}
        </span>
        <FocusDeployButton deploy={item.deploy} label={item.ctaLabel} />
      </div>
    );
  }
  return (
    <div className="group/row relative -mx-2 flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-1.5 transition-colors hover:bg-secondary">
      <Link
        href={item.ctaHref}
        variant="unstyled"
        aria-label={`${item.ctaLabel}: ${item.title}`}
        className="absolute inset-0 rounded-md outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
      />
      <span className={cn("size-1.5 shrink-0 rounded-full", DOT[item.intent])} aria-hidden />
      <span className="min-w-0 flex-1 truncate type-body">
        <span className="text-foreground">{item.title}</span>
        {item.meta ? <span className="text-muted-foreground"> · {item.meta}</span> : null}
      </span>
      <span className="inline-flex shrink-0 items-center gap-1 type-body font-medium text-primary">
        {item.ctaLabel}
        <Icon name="arrow-right" size="xs" className="transition-transform group-hover/row:translate-x-0.5" />
      </span>
    </div>
  );
}

// ── the no-graph blank states (the approved /styleguide/focus-sections picks) ──

/** Not-started domain: a GHOST of the trend that will live here once setup's done. */
function SetupGhostFrame({ domain }: { domain: FocusSectionDomain }) {
  const { done, total } = domain.setup ?? { done: 0, total: 4 };
  const steps = Math.min(total, 3);
  return (
    <div className="relative w-full" style={{ height: 96 }}>
      <svg viewBox="0 0 7 100" preserveAspectRatio="none" className="absolute inset-0 h-full w-full" aria-hidden>
        <line x1="0" y1={100 - TARGET} x2="7" y2={100 - TARGET} stroke="currentColor" className="text-foreground/15" strokeDasharray="3 3" strokeWidth="1" vectorEffect="non-scaling-stroke" />
        <polyline points="0,88 2,78 4,64 7,46" fill="none" stroke="currentColor" className="text-muted-foreground/30" strokeWidth="1.5" strokeDasharray="4 4" strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
        <circle cx="0" cy="88" r="2.5" className="fill-muted-foreground/40" vectorEffect="non-scaling-stroke" />
      </svg>
      <span className="pointer-events-none absolute right-0 -translate-y-1/2 type-subtext tabular-nums text-muted-foreground/50" style={{ top: `${100 - TARGET}%` }}>
        {TARGET} target
      </span>
      <div className="absolute inset-0 flex items-center justify-center px-4">
        <EmptyState
          variant="inline"
          className="[&_[data-slot=empty-state-body]]:py-0"
          icon={<Icon name="trending-up" size="sm" className="text-muted-foreground/50" />}
          title={
            <>
              Your{" "}
              <span className="text-foreground">{domain.label}</span>{" "}
              health trend starts once setup&apos;s done
            </>
          }
          description={
            <span className="tabular-nums">
              {done} of {steps} first steps
            </span>
          }
        />
      </div>
    </div>
  );
}

/** Cross-cutting domain: no chart (it'll never have one trend) — name the areas
 *  it touches, each with its live pulse health dot. */
function SpannedAreas({ domain }: { domain: FocusSectionDomain }) {
  const spans = domain.spans ?? [];
  return (
    <div className="flex w-full flex-col justify-center gap-2.5" style={{ minHeight: 72 }}>
      <span className="inline-flex items-center gap-1.5 type-subtext">
        <Icon name="layout-grid" size="xs" className="shrink-0 text-muted-foreground/70" />
        <span className="text-muted-foreground">
          {spans.length > 0 ? (
            <>
              <span className="text-foreground">
                Touches {spans.length} area{spans.length === 1 ? "" : "s"}
              </span>{" "}
              — fix once, helps each
            </>
          ) : (
            <>
              <span className="text-foreground">Workspace-wide</span> — keeps several areas healthy
            </>
          )}
        </span>
      </span>
      {spans.length > 0 ? (
        <div className="flex flex-wrap items-center gap-1.5">
          {spans.map((s) => (
            <span
              key={s.key}
              className="inline-flex items-center gap-1.5 rounded-full border border-border/60 bg-card px-2 py-0.5 type-subtext text-muted-foreground"
            >
              <span aria-hidden className={cn("size-1.5 shrink-0 rounded-full", DOT[s.intent])} />
              {s.label}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}

// ── the domain Card ──────────────────────────────────────────────────────────

function DomainCard({ domain }: { domain: FocusSectionDomain }) {
  return (
    <Card className="gap-0 overflow-hidden py-0">
      <CardContent className="flex flex-col gap-3 px-5 py-4">
        <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
          <AreaTag domain={domain} />
          <Magnitude domain={domain} />
          {domain.href ? (
            <Button asChild size="xs" variant="ghost" className="ml-auto -mr-2 shrink-0">
              <NextLink href={domain.href}>
                {domain.kind === "crosscutting" ? "Open" : `Open ${domain.label}`}
                <Icon name="arrow-right" size="xs" />
              </NextLink>
            </Button>
          ) : null}
        </div>

        {domain.kind === "atrisk" ? (
          domain.history.length >= 2 ? (
            <>
              <HealthSparkline area={domainAsArea(domain)} height={96} labels />
              <LatestNote domain={domain} />
            </>
          ) : (
            <LatestNote domain={domain} />
          )
        ) : domain.kind === "setup" ? (
          <SetupGhostFrame domain={domain} />
        ) : (
          <SpannedAreas domain={domain} />
        )}

        {domain.items.length > 0 ? (
          <div className="flex flex-col gap-0.5 border-t border-border/40 pt-3">
            {domain.items.map((it) => (
              <FocusActionRow key={it.key} item={it} />
            ))}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

function GroupHeader({ group }: { group: FocusSectionGroup }) {
  const sub =
    group.atRiskCount > 0
      ? `${group.atRiskCount} area${group.atRiskCount === 1 ? "" : "s"} below target`
      : "setup";
  return (
    <div className="flex flex-wrap items-baseline gap-x-2 px-1 pt-1">
      <span className="type-body font-medium text-foreground">{group.modeLabel}</span>
      <span className="type-subtext text-muted-foreground">{sub}</span>
    </div>
  );
}

export function FocusSections({ groups, crosscutting, className }: FocusSectionsProps) {
  // A single mode in scope (a mode dashboard) doesn't need its own name as a
  // header — the page already is that mode. Show headers only when grouping ≥2.
  const showHeaders = groups.length > 1;
  return (
    <div className={cn("flex flex-col gap-5", className)}>
      {groups.map((group) => (
        <div key={group.modeKey} className="flex flex-col gap-2">
          {showHeaders ? <GroupHeader group={group} /> : null}
          <div className="flex flex-col gap-3">
            {group.domains.map((d) => (
              <DomainCard key={d.key} domain={d} />
            ))}
          </div>
        </div>
      ))}
      {crosscutting ? (
        <div className="flex flex-col gap-2">
          {showHeaders ? (
            <div className="flex flex-wrap items-baseline gap-x-2 px-1 pt-1">
              <span className="type-body font-medium text-foreground">Across the workspace</span>
              <span className="type-subtext text-muted-foreground">not tied to one area</span>
            </div>
          ) : null}
          <DomainCard domain={crosscutting} />
        </div>
      ) : null}
    </div>
  );
}
